/**
 * Shared icon resolution for dynamic list fields.
 *
 * Custom-list fields always carry an authoritative type, so we don't
 * need any name-based heuristics:
 *
 *   - `dataOrigin: 1` (process form fields) → `field.type` carries the
 *     form-builder type (`text`, `date`, `selector-user`, …).
 *   - `dataOrigin: 2` (dynamic-entity fields) → `entity.fields[].type`
 *     uses the same vocabulary.
 *
 * Both branches surface as `field.fieldType` to this module (callers
 * normalise the lookup); reserved CustomList cell types — process
 * name, instance status, applicant, etc. — surface as `field.cellType`.
 *
 * Resolution order, most → least authoritative:
 *   1. `field.fieldType`   → FIELD_TYPE_ICON
 *   2. `field.cellType`    → CELL_TYPE_ICON (when not the generic 'field')
 *   3. `FontSizeOutlined`  → fallback (treat unknowns as plain text)
 */

import {
  AimOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DatabaseOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FieldNumberOutlined,
  FileOutlined,
  FontSizeOutlined,
  FunctionOutlined,
  GlobalOutlined,
  GroupOutlined,
  IdcardOutlined,
  LinkOutlined,
  MailOutlined,
  NumberOutlined,
  PercentageOutlined,
  PhoneOutlined,
  PictureOutlined,
  ProfileOutlined,
  SendOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';

// Icons for reserved CustomList cell types (`process-name`,
// `instance-status`, …). This is the single source of truth — the
// list-builder's `FieldRow` resolves its icons through this module
// too, so adding a new cell-type icon here propagates to the table
// header, the mosaic cards and the builder field row at once.
//
// We deliberately keep the map local (instead of pulling it from
// `list-builder/utils`) because that file runs `getIntl()` at
// module-load time, which would break when this module is imported
// from the DVA model graph (models are evaluated during bootstrap,
// before umi finishes wiring up `getIntl`).
const CELL_TYPE_ICON = {
  'process-name': ProfileOutlined,
  'process-group-name': GroupOutlined,
  'instance-name': BarcodeOutlined,
  'instance-created': ClockCircleOutlined,
  'instance-status': ThunderboltOutlined,
  'applicant-full-name': UserOutlined,
  'approved-date': CalendarOutlined,
  'rejected-date': CalendarOutlined,
  'applicant-names': UserOutlined,
  'applicant-last-names': UserOutlined,
  'applicant-email': MailOutlined,
  'applicant-position': IdcardOutlined,
  'instance-identifier': BarcodeOutlined,
  variable: FunctionOutlined,
  'instance-comments-count': CommentOutlined,
  'applicant-area': TeamOutlined,
  'entity-field': DatabaseOutlined,
  'entity-item-identifier': BarcodeOutlined,
  'entity-item-created': ClockCircleOutlined,
  'entity-item-is-active': CheckSquareOutlined,
  'entity-item-user-creator': UserOutlined,
  'entity-item-user-modifier': UserOutlined,
  'instance-current-action': AimOutlined,
  'instance-goto-action': SendOutlined,
  'instance-percent-completed': PercentageOutlined,
};

// Icon by form-builder / dynamic-entity field type (`field.fieldType`).
// Keys cover both the kebab-cased form-builder vocabulary
// (`selector-user`, `date-time`…) and the simpler entity-field
// vocabulary (`text`, `number`, `boolean`…). Aliases are listed
// explicitly so we don't need any name-based guessing downstream.
export const FIELD_TYPE_ICON = {
  // text-flavoured
  text: FontSizeOutlined,
  string: FontSizeOutlined,
  varchar: FontSizeOutlined,
  char: FontSizeOutlined,
  input: FontSizeOutlined,
  textarea: ProfileOutlined,
  'text-area': ProfileOutlined,
  richtext: ProfileOutlined,
  'rich-text': ProfileOutlined,
  signature: ProfileOutlined,

  // date / time
  date: CalendarOutlined,
  datetime: CalendarOutlined,
  'date-time': CalendarOutlined,
  datetimerange: CalendarOutlined,
  'date-range': CalendarOutlined,
  daterange: CalendarOutlined,
  time: ClockCircleOutlined,

  // numeric
  number: FieldNumberOutlined,
  decimal: FieldNumberOutlined,
  integer: NumberOutlined,
  int: NumberOutlined,
  long: NumberOutlined,
  currency: DollarOutlined,
  money: DollarOutlined,
  percentage: PercentageOutlined,
  percent: PercentageOutlined,

  // boolean
  boolean: CheckSquareOutlined,
  bool: CheckSquareOutlined,
  switch: CheckSquareOutlined,

  // contact
  email: MailOutlined,
  phone: PhoneOutlined,
  url: LinkOutlined,

  // selectors / catalogues
  'selector-user': IdcardOutlined,
  'selector-user-multiple': IdcardOutlined,
  'selector-position': TeamOutlined,
  'selector-position-multiple': TeamOutlined,
  'selector-area': TeamOutlined,
  'selector-area-multiple': TeamOutlined,
  'selector-user-group': TeamOutlined,
  'selector-role': TeamOutlined,
  'selector-entity': TagsOutlined,
  'selector-entity-multiple': TagsOutlined,
  'selector-list': TagsOutlined,
  selector: TagsOutlined,

  // files / media
  file: FileOutlined,
  upload: FileOutlined,
  attachment: FileOutlined,
  image: PictureOutlined,

  // geo
  location: EnvironmentOutlined,
  geolocation: EnvironmentOutlined,
  address: EnvironmentOutlined,
  country: GlobalOutlined,
};

/**
 * Resolves the Ant Design icon component for a "field-shaped" descriptor.
 * Always returns a component (never null) — falls back to FontSizeOutlined
 * because most generic, untyped columns are stored as plain text on the
 * backend and a "text" glyph reads more honestly than a "tag" one.
 *
 * @param {Object} field
 * @param {string} [field.fieldType] - form-builder / entity-field type
 * @param {string} [field.cellType]  - CustomList cell type
 * @returns {React.ComponentType}
 */
export const iconForField = (field) => {
  const fromType = FIELD_TYPE_ICON[field?.fieldType];
  if (fromType) return fromType;

  if (field?.cellType && field.cellType !== 'field') {
    const fromCellType = CELL_TYPE_ICON[field.cellType];
    if (fromCellType) return fromCellType;
  }

  return FontSizeOutlined;
};

/**
 * Antd Table-flavoured adaptation: a column descriptor doesn't carry the
 * original `fieldType`. We resolve it from the source field by
 * `dataIndex` (= `internalName`) inside the original config payload and
 * then delegate to {@link iconForField}.
 *
 * @param {Object} column - Antd Table column descriptor
 * @param {Object[]} [sourceFields] - the original CustomList `fields` array
 * @returns {React.ComponentType}
 */
export const iconForColumn = (column, sourceFields = []) => {
  const original = sourceFields.find((f) => f.internalName === column?.dataIndex) || {};
  return iconForField({
    fieldType: original?.dynamicEntityField?.type || original?.field?.type,
    // `column.cellType` is the CustomList cell type (`entity-field`,
    // `instance-status`, …); `column.type` now carries the resolved
    // form-builder data type. Prefer the explicit `cellType` field
    // and fall back to `type` for older callers.
    cellType: column?.cellType ?? column?.type,
  });
};
